import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Divider, Container, Paper } from '@mui/material';
import { ArrowBack as BackIcon, Gavel as GavelIcon, CheckCircle as CheckIcon } from '@mui/icons-material';

const BLUE = '#00A8E8';
const GREEN = '#86E000';
const DARK_BLUE = '#005B8F';

const consentItems = [
  'I have read and understood the information provided to me.',
  'I voluntarily agree to participate in the study.',
  'I understand that AI face scan outputs are screening and wellness indicators and not a substitute for clinical diagnosis.',
  'I understand that the accuracy of the face scan can vary, and that concerning results will be confirmed by standard measurements.',
  'I consent to the recording and secure storage of a facial video and the measurements derived from it, for this research only.',
];

const platformTerms = [
  {
    title: '1. Acceptance of Terms',
    body: 'By accessing or using the Eros Wellness AI Clinic Platform ("Platform"), you agree to be bound by these Terms and Conditions. If you do not agree, please do not use the Platform.',
  },
  {
    title: '2. Platform Use',
    body: 'The Platform is intended for authorised healthcare administrators, staff, and patients at registered screening locations. Unauthorised access, sharing of credentials, or use of the Platform for any unlawful purpose is strictly prohibited.',
  },
  {
    title: '3. Health Information Disclaimer',
    bullets: [
      'Results provided by the Platform are for screening and wellness indication purposes only.',
      'AI-assisted outputs are not a substitute for clinical diagnosis by a qualified healthcare professional.',
      'Concerning results must be confirmed through standard clinical measurements.',
      'Eros Wellness does not provide medical advice, diagnosis, or treatment.',
    ],
  },
  {
    title: '4. Patient Consent',
    body: 'All patients must provide informed consent before undergoing any screening. The consent covers the collection of vitals, biometric data (where applicable), and AI-assisted health indicators. Consent can be withdrawn at any time by contacting privacy@erosuniverse.com.',
  },
  {
    title: '5. Intellectual Property',
    body: 'All content, software, algorithms, reports, and branding on the Platform are the exclusive property of Eros Universe. No part may be reproduced, distributed, or used for commercial purposes without written permission.',
  },
  {
    title: '6. Limitation of Liability',
    body: 'Eros Universe shall not be liable for any indirect, incidental, or consequential damages arising from the use of the Platform, inaccuracies in screening data, or reliance on AI-generated health indicators without clinical confirmation.',
  },
  {
    title: '7. Governing Law',
    body: 'These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra.',
  },
  {
    title: '8. Changes to Terms',
    body: 'Eros Universe reserves the right to update these Terms at any time. Continued use of the Platform after changes constitutes acceptance of the revised Terms. The current version and date will always be displayed on this page.',
  },
];

export default function TermsAndConditions() {
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f7fa', pb: 8 }}>
      {/* Header */}
      <Box sx={{ background: `linear-gradient(135deg, ${DARK_BLUE} 0%, #007BB5 100%)`, py: 5, px: 3 }}>
        <Container maxWidth="md">
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(-1)}
            sx={{ color: 'rgba(255,255,255,0.8)', mb: 3, '&:hover': { color: '#fff' } }}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '12px',
              background: `linear-gradient(135deg, ${BLUE}, ${GREEN})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <GavelIcon sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', letterSpacing: 2, textTransform: 'uppercase' }}>
                Eros Wellness
              </Typography>
              <Typography variant="h4" sx={{ color: '#fff', fontWeight: 800, fontFamily: 'Montserrat, sans-serif' }}>
                Terms & Conditions
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', mt: 1 }}>
            Last updated: September 2026 &nbsp;·&nbsp; Version 1.0
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 4 }}>
        {/* Patient Consent Section */}
        <Paper elevation={0} sx={{ borderRadius: 3, p: { xs: 3, sm: 5 }, border: `1px solid ${GREEN}44`, mb: 3, bgcolor: '#f0fdf4' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#16a34a', mb: 2, fontFamily: 'Montserrat, sans-serif' }}>
            Patient Consent Agreement
          </Typography>
          <Typography sx={{ color: '#475569', fontSize: '0.9rem', mb: 3, lineHeight: 1.7 }}>
            Before using the Eros Wellness screening kiosk, every patient must acknowledge and agree to the following:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {consentItems.map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <CheckIcon sx={{ color: '#16a34a', fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                <Typography sx={{ color: '#1e293b', fontSize: '0.9rem', lineHeight: 1.7 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Platform Terms */}
        <Paper elevation={0} sx={{ borderRadius: 3, p: { xs: 3, sm: 5 }, border: '1px solid #e2e8f0' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: DARK_BLUE, mb: 3, fontFamily: 'Montserrat, sans-serif' }}>
            Platform Terms of Use
          </Typography>

          {platformTerms.map((sec, idx) => (
            <Box key={idx} sx={{ mb: 3.5 }}>
              <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem', mb: 1 }}>
                {sec.title}
              </Typography>

              {sec.body && (
                <Typography sx={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.8 }}>
                  {sec.body}
                </Typography>
              )}

              {sec.bullets && (
                <Box component="ul" sx={{ pl: 3, mt: 0.5, mb: 0 }}>
                  {sec.bullets.map((b, i) => (
                    <Typography component="li" key={i} sx={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.8, mb: 0.5 }}>
                      {b}
                    </Typography>
                  ))}
                </Box>
              )}

              {idx < platformTerms.length - 1 && <Divider sx={{ mt: 2.5 }} />}
            </Box>
          ))}

          {/* Contact */}
          <Box sx={{ mt: 2, p: 3, borderRadius: 2, bgcolor: '#f0f9ff', border: `1px solid ${BLUE}22` }}>
            <Typography sx={{ fontWeight: 700, color: DARK_BLUE, mb: 0.5 }}>Questions or Concerns?</Typography>
            <Typography sx={{ color: '#475569', fontSize: '0.9rem' }}>
              Contact us at{' '}
              <Box component="a" href="mailto:privacy@erosuniverse.com" sx={{ color: BLUE, textDecoration: 'none', fontWeight: 600 }}>
                privacy@erosuniverse.com
              </Box>
            </Typography>
          </Box>
        </Paper>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#94a3b8', mt: 3 }}>
          Eros Wellness Platform © {new Date().getFullYear()} Eros Universe · All rights reserved
        </Typography>
      </Container>
    </Box>
  );
}
